// UserPreferencesManager.js - Gestionnaire centralisé des préférences utilisateur
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Gestionnaire centralisé des préférences utilisateur
 * Gère la persistance et la synchronisation des paramètres entre les composants
 */
class UserPreferencesManager {
  constructor() {
    this.cache = new Map();
    this.listeners = new Set();
  }

  /**
   * Structure par défaut des préférences utilisateur
   */
  getDefaultPreferences() {
    return {
      // Localisation
      language: 'fr',
      country: 'MA',
      timezone: 'Africa/Casablanca',
      
      // IA et Voix
      aiVoice: 'Marie (Féminine, FR)',
      aiEngine: 'Claude (Anthropic)',
      
      // Recherche Web
      searchEngine: 'Google',
      preferredDomains: [],
      enableGeographicLocalization: true,
      localGlobalRatio: 0.7,
      
      // Notifications
      notifications: {
        importantNews: true,
        communityMessages: true,
        improvementSuggestions: true,
        productUpdates: true
      },
      
      // Préférences de recherche avancées
      search: {
        maxSources: 20,
        preferredTypes: ['Educational', 'Governmental', 'Commercial'],
        useAI: true,
        enableSemanticAnalysis: true,
        prioritizeLocal: true,
        includeAcademic: true,
        includeSocial: false
      },
      
      // Objectif courant
      currentObjective: {
        id: null,
        title: null,
        theme: null,
        chapitre: null,
        selectedAt: null,
        lastAccessed: null
      },
      
      // Métadonnées
      lastUpdated: new Date().toISOString(),
      version: '1.0'
    };
  }

  /**
   * Récupère les préférences utilisateur depuis Firestore
   */
  async getUserPreferences(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Vérifier le cache d'abord
    if (this.cache.has(userId)) {
      const cached = this.cache.get(userId);
      // Cache valide pour 5 minutes
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) {
        return cached.preferences;
      }
    }

    try {
      const preferencesRef = doc(db, 'users', userId, 'settings', 'preferences');
      const preferencesSnap = await getDoc(preferencesRef);

      let preferences;
      if (preferencesSnap.exists()) {
        const data = preferencesSnap.data();
        // Fusionner avec les valeurs par défaut pour s'assurer que toutes les clés existent
        preferences = this.mergeWithDefaults(data);
      } else {
        // Créer les préférences par défaut si elles n'existent pas
        preferences = this.getDefaultPreferences();
        await this.saveUserPreferences(userId, preferences);
      }

      // Mettre en cache
      this.cache.set(userId, {
        preferences,
        timestamp: Date.now()
      });

      return preferences;
    } catch (error) {
      console.error('Erreur lors de la récupération des préférences:', error);
      // Retourner les valeurs par défaut en cas d'erreur
      return this.getDefaultPreferences();
    }
  }

  /**
   * Sauvegarde les préférences utilisateur dans Firestore
   */
  async saveUserPreferences(userId, preferences) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const preferencesRef = doc(db, 'users', userId, 'settings', 'preferences');
      const updatedPreferences = {
        ...preferences,
        lastUpdated: new Date().toISOString()
      };

      await setDoc(preferencesRef, updatedPreferences, { merge: true });

      // Mettre à jour le cache
      this.cache.set(userId, {
        preferences: updatedPreferences,
        timestamp: Date.now()
      });

      // Notifier les listeners
      this.notifyListeners(userId, updatedPreferences);

      return updatedPreferences;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des préférences:', error);
      throw error;
    }
  }

  /**
   * Met à jour partiellement les préférences utilisateur
   */
  async updateUserPreferences(userId, updates) {
    try {
      const currentPreferences = await this.getUserPreferences(userId);
      const updatedPreferences = this.deepMerge(currentPreferences, updates);
      return await this.saveUserPreferences(userId, updatedPreferences);
    } catch (error) {
      console.error('Erreur lors de la mise à jour des préférences:', error);
      throw error;
    }
  }

  /**
   * Récupère les paramètres de recherche optimisés pour SourceManager
   */
  async getSearchParameters(userId, competenceId = null) {
    try {
      const preferences = await this.getUserPreferences(userId);
      
      // Configuration des langues par pays (depuis votre configuration existante)
      const countryLanguageMap = {
        'MA': { primary: ['fr', 'ar'], secondary: ['en'], domains: ['.ma', '.org.ma', '.gov.ma'] },
        'FR': { primary: ['fr'], secondary: ['en'], domains: ['.fr', '.gouv.fr', '.edu.fr'] },
        'US': { primary: ['en'], secondary: ['es'], domains: ['.com', '.gov', '.edu'] },
        'CA': { primary: ['en', 'fr'], secondary: [], domains: ['.ca', '.gc.ca', '.edu.ca'] },
        'ES': { primary: ['es'], secondary: ['en'], domains: ['.es', '.gob.es', '.edu.es'] },
        'DE': { primary: ['de'], secondary: ['en'], domains: ['.de', '.gov.de', '.edu.de'] },
        'IT': { primary: ['it'], secondary: ['en'], domains: ['.it', '.gov.it', '.edu.it'] },
        'UK': { primary: ['en'], secondary: [], domains: ['.uk', '.gov.uk', '.ac.uk'] },
        'BR': { primary: ['pt'], secondary: ['en'], domains: ['.br', '.gov.br', '.edu.br'] },
        'JP': { primary: ['ja'], secondary: ['en'], domains: ['.jp', '.go.jp', '.ac.jp'] },
        'CN': { primary: ['zh'], secondary: ['en'], domains: ['.cn', '.gov.cn', '.edu.cn'] }
      };

      const countryConfig = countryLanguageMap[preferences.country] || countryLanguageMap['MA'];
      
      // Construire les paramètres de recherche optimisés
      const searchParams = {
        // Paramètres de base
        language: preferences.language,
        country: preferences.country,
        maxSources: preferences.search.maxSources,
        useAI: preferences.search.useAI,
        
        // Langues optimisées
        languages: [
          ...countryConfig.primary,
          ...countryConfig.secondary,
          preferences.language // S'assurer que la langue préférée est incluse
        ].filter((lang, index, arr) => arr.indexOf(lang) === index), // Supprimer les doublons
        
        // Géolocalisation
        enableGeographicLocalization: preferences.enableGeographicLocalization,
        localGlobalRatio: preferences.localGlobalRatio,
        preferredDomains: [
          ...countryConfig.domains,
          ...preferences.preferredDomains
        ],
        
        // Types de sources préférés
        preferredTypes: preferences.search.preferredTypes,
        
        // Moteur de recherche
        searchEngine: preferences.searchEngine,
        
        // Paramètres avancés
        enableSemanticAnalysis: preferences.search.enableSemanticAnalysis,
        prioritizeLocal: preferences.search.prioritizeLocal,
        includeAcademic: preferences.search.includeAcademic,
        includeSocial: preferences.search.includeSocial,
        
        // Métadonnées
        lastUpdated: preferences.lastUpdated,
        fromPreferences: true
      };

      console.log('Paramètres de recherche générés:', searchParams);
      return searchParams;
      
    } catch (error) {
      console.error('Erreur lors de la génération des paramètres de recherche:', error);
      // Retourner des paramètres par défaut
      return {
        language: 'fr',
        country: 'MA',
        languages: ['fr', 'ar', 'en'],
        maxSources: 20,
        useAI: true,
        enableGeographicLocalization: true,
        localGlobalRatio: 0.7,
        preferredTypes: ['Educational', 'Governmental'],
        searchEngine: 'Google',
        fromPreferences: false
      };
    }
  }

  /**
   * Ajoute un listener pour les changements de préférences
   */
  addPreferencesListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notifie tous les listeners des changements
   */
  notifyListeners(userId, preferences) {
    this.listeners.forEach(listener => {
      try {
        listener(userId, preferences);
      } catch (error) {
        console.error('Erreur dans un listener de préférences:', error);
      }
    });
  }

  /**
   * Fusionne les préférences avec les valeurs par défaut
   */
  mergeWithDefaults(userPreferences) {
    const defaults = this.getDefaultPreferences();
    return this.deepMerge(defaults, userPreferences);
  }

  /**
   * Fusion profonde d'objets
   */
  deepMerge(target, source) {
    const result = { ...target };
    
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    
    return result;
  }

  /**
   * Invalide le cache pour un utilisateur
   */
  invalidateCache(userId) {
    if (userId) {
      this.cache.delete(userId);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Exporte les préférences utilisateur
   */
  async exportUserPreferences(userId) {
    const preferences = await this.getUserPreferences(userId);
    return {
      preferences,
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
  }

  /**
   * Importe les préférences utilisateur
   */
  async importUserPreferences(userId, importedData) {
    if (!importedData.preferences) {
      throw new Error('Données d\'importation invalides');
    }

    const mergedPreferences = this.mergeWithDefaults(importedData.preferences);
    return await this.saveUserPreferences(userId, mergedPreferences);
  }

  /**
   * Définit l'objectif courant pour un utilisateur
   */
  async setCurrentObjective(userId, objectiveData) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const objectiveUpdate = {
      currentObjective: {
        id: objectiveData?.id || null,
        title: objectiveData?.title || null,
        theme: objectiveData?.theme || null,
        chapitre: objectiveData?.chapitre || null,
        selectedAt: new Date().toISOString(),
        lastAccessed: new Date().toISOString()
      }
    };

    return await this.updateUserPreferences(userId, objectiveUpdate);
  }

  /**
   * Récupère l'objectif courant pour un utilisateur
   */
  async getCurrentObjective(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const preferences = await this.getUserPreferences(userId);
      return preferences.currentObjective;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'objectif courant:', error);
      return this.getDefaultPreferences().currentObjective;
    }
  }

  /**
   * Efface l'objectif courant pour un utilisateur
   */
  async clearCurrentObjective(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const objectiveUpdate = {
      currentObjective: this.getDefaultPreferences().currentObjective
    };

    return await this.updateUserPreferences(userId, objectiveUpdate);
  }

  /**
   * Met à jour la date de dernier accès de l'objectif courant
   */
  async updateLastAccessed(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      const currentObjective = await this.getCurrentObjective(userId);
      if (currentObjective.id) {
        const objectiveUpdate = {
          currentObjective: {
            ...currentObjective,
            lastAccessed: new Date().toISOString()
          }
        };
        return await this.updateUserPreferences(userId, objectiveUpdate);
      }
      return currentObjective;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du dernier accès:', error);
      throw error;
    }
  }

  /**
   * Vérifie si un objectif est actuellement sélectionné
   */
  async hasCurrentObjective(userId) {
    if (!userId) {
      return false;
    }

    try {
      const currentObjective = await this.getCurrentObjective(userId);
      return !!(currentObjective && currentObjective.id);
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'objectif courant:', error);
      return false;
    }
  }

  /**
   * Compare l'objectif courant avec un ID donné
   */
  async isCurrentObjective(userId, objectiveId) {
    if (!userId || !objectiveId) {
      return false;
    }

    try {
      const currentObjective = await this.getCurrentObjective(userId);
      return currentObjective.id === objectiveId;
    } catch (error) {
      console.error('Erreur lors de la comparaison des objectifs:', error);
      return false;
    }
  }
}

// Instance singleton
const userPreferencesManager = new UserPreferencesManager();

export default userPreferencesManager;
